'use no memo';

import {
  closeCurrentDialog,
  FormControl,
  FormField,
  IconButton,
  Label,
  openDialog,
  Separator,
} from 'platform/components';
import {Box, Hide, HStack, Show, VStack} from 'platform/foundation';

import {useRef} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {always, concat, isNil, times} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetTireWarehouseSettingsQuery} from '@dms/api/core';
import {CustomerResponseBodyV2, useGetCustomerV2Query} from '@dms/api/customer';
import {OccupiedSpaceRequestBody} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useEditCustomer} from '../../../hooks/useEditCustomer';
import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {usePhoneNumbers} from '../../../hooks/usePhoneNumbers';
import {getCustomerName} from '../../../utils/getCustomerName';
import {getNaturalPersonFullName} from '../../../utils/getNaturalPersonFullName';
import {getPhoneNumber} from '../../../utils/getPhoneNumber';
import {CustomerSelectSearch, SaneCustomer} from '../../CustomerSelectSearch/CustomerSelectSearch';
import {CustomerMatchOrCreate} from '../../CustomerWidgetCard/components/CustomerMatchOrCreate';
import {EMPTY_WHEEL} from '../constants/emptyWheel';
import {useTireSetOptions} from '../hooks/useTireSetOptions';
import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';
import {Wheel} from '../types/Wheel';
import {StorageWarehouse} from './StorageWarehouse';

interface TireSetFormHeaderProps extends TestIdProps {
  control: FormControl<TireSetDetailFormDefinition>;
  homeBranchId: string | Nullish;
  formApi: UseFormReturn<TireSetDetailFormDefinition>;
  minNumberOfWheels?: number;
  tireSetId?: string;
  tireOrderId: string;
  onSelectStorageLocationWithoutId?: (selectedSpace: OccupiedSpaceRequestBody | undefined) => void;
  selectStorageLocationWithoutId?: OccupiedSpaceRequestBody;
  tireSetState?: string | Nullish;
}

export function TireSetFormHeader(props: TireSetFormHeaderProps) {
  const tireSetOptions = useTireSetOptions(props.homeBranchId);
  const customerRowRef = useRef<HTMLDivElement>(null);

  const {countriesOptions} = usePhoneNumbers();

  const customerId =
    useWatch({control: props.formApi.control, name: 'contactInformation.id'}) ?? '';
  const {data: customerData} = useGetCustomerV2Query(
    {customerId},
    {skip: isNilOrEmpty(customerId)}
  );

  const [hasReadTireWarehousePermission] = usePermissions({
    permissionKeys: ['readTireWarehouse'],
    scopes: {
      readTireWarehouse: {
        // Skipping scope, BE handles data for tire warehouse
        tireWarehouses: 'SKIP',
      },
    },
  });

  const {data: tireWarehouseSettings} = useGetTireWarehouseSettingsQuery();

  const handleNumberTiresChange = (value: number | null) => {
    if (isNil(value)) {
      return;
    }

    const wheels = props.formApi.getValues('wheels');

    let wheelsArray: Wheel[];

    if (value > wheels.length) {
      const dummyWheels = times(always(EMPTY_WHEEL), value - wheels.length);

      const fullWheels = dummyWheels.map((_, index) => ({
        ...EMPTY_WHEEL,
        manufacturer: tireSetOptions.defaultManufacturer?.id ?? undefined,
        location:
          tireSetOptions.locationOptions?.[
            (index + wheels.length) % tireSetOptions.locationOptions?.length
          ]?.value,
        hasHubcap: false,
        hasWheelLocks: false,
      }));

      wheelsArray = concat(wheels, fullWheels);
    } else {
      wheelsArray = wheels.slice(0, value);
    }

    props.formApi.setValue('wheels', wheelsArray);
  };

  const setCustomerValuesIntoForm = (data: Partial<SaneCustomer>) => {
    const formattedPhoneNumber = getPhoneNumber(
      data.prefix,
      data.primaryContact?.phoneNumber ?? data.phoneNumber,
      countriesOptions
    );
    const updatedContactInformation = {
      id: data.customerId,
      name: data.contactPersonName ?? data.primaryContact?.name ?? data.name,
      email: data.primaryContact?.email ?? data.email,
      phoneNumber: {
        countryCode: formattedPhoneNumber.countryCode || 'CZE',
        number: formattedPhoneNumber.number || '',
        prefix: formattedPhoneNumber.prefix || '',
      },
    };

    props.formApi.setValue('contactInformation.name', updatedContactInformation.name);
    props.formApi.setValue('contactInformation.email', updatedContactInformation.email);
    props.formApi.setValue('contactInformation.phoneNumber', updatedContactInformation.phoneNumber);
    props.formApi.setValue('contactInformation.id', updatedContactInformation.id);
  };

  const handleCustomerCreated = (customer: CustomerResponseBodyV2) => {
    const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];

    if (!customerInfo) {
      return;
    }

    const email = customerInfo.emails?.[0]?.email;
    const phoneNumber = customerInfo.phoneNumbers?.[0];
    const name = getCustomerName(customer);
    const contactPersonName = getNaturalPersonFullName(customerInfo);

    setCustomerValuesIntoForm({
      name: name ?? '',
      contactPersonName: contactPersonName ?? '',
      email: email ?? '',
      prefix: phoneNumber?.prefix ?? '',
      phoneNumber: phoneNumber?.number ?? '',
      customerId: customer.id,
    });

    closeCurrentDialog();
  };

  const handleCreateCustomer = () => {
    openDialog(
      <CustomerMatchOrCreate
        onCustomer={handleCustomerCreated}
        data-testid={suffixTestId('createCustomer', props)}
      />,
      {
        title: i18n.t('entity.customer.labels.customer'),
      }
    );
  };

  const editCustomer = useEditCustomer(customerData, (customer) => {
    const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];

    if (!customerInfo) {
      return;
    }

    const email = customerInfo.emails?.[0]?.email;
    const phoneNumber = customerInfo.phoneNumbers?.[0];
    const name = getCustomerName(customer);
    const contactPersonName = getNaturalPersonFullName(customerInfo);

    setCustomerValuesIntoForm({
      name: name ?? '',
      contactPersonName: contactPersonName ?? '',
      email: email ?? '',
      prefix: phoneNumber?.prefix ?? '',
      phoneNumber: phoneNumber?.number ?? '',
      customerId: customer.id,
    });

    closeCurrentDialog();
  });

  const handleEditCustomer = () => {
    editCustomer?.();
  };

  return (
    <VStack spacing={4}>
      <Box ref={customerRowRef} width="100%">
        <HStack spacing={4} align="flex-end">
          <FormField name="contactInformation.id" type="hidden" control={props.control} />
          <Box flex={1}>
            <CustomerSelectSearch
              onCustomerSelect={setCustomerValuesIntoForm}
              // eslint-disable-next-line react-hooks/refs
              customPopperWidth={customerRowRef.current?.clientWidth ?? 0}
            />
          </Box>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="contactInformation.name"
              type="text"
              label={i18n.t('entity.businessCase.labels.contactPerson')}
              data-testid={suffixTestId('contactPersonName', props)}
              isDisabled
            />
          </Box>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="contactInformation.phoneNumber"
              type="phone"
              countries={countriesOptions}
              label={i18n.t('general.labels.phone')}
              data-testid={suffixTestId('contactPersonPhone', props)}
              isDisabled
            />
          </Box>
          <Box flex={1}>
            <HStack spacing={2} align="flex-end">
              <Box flex={1}>
                <FormField
                  control={props.control}
                  name="contactInformation.email"
                  type="email"
                  label={i18n.t('general.labels.email')}
                  data-testid={suffixTestId('contactPersonEmail', props)}
                  isDisabled
                />
              </Box>
              <HStack spacing={1}>
                <IconButton
                  onClick={handleEditCustomer}
                  priority="secondary"
                  icon="image/edit"
                  data-testid={suffixTestId('editCustomer', props)}
                  isDisabled={isNilOrEmpty(customerId)}
                />
                <IconButton
                  onClick={handleCreateCustomer}
                  priority="secondary"
                  icon="content/add_circle"
                  data-testid={suffixTestId('createCustomer', props)}
                />
              </HStack>
            </HStack>
          </Box>
        </HStack>
      </Box>
      <Separator spacing={0} />
      <HStack spacing={4}>
        <Box flex={1}>
          <FormField
            control={props.control}
            name="categoryId"
            type="choice"
            label={i18n.t('entity.tireSet.labels.category')}
            options={tireSetOptions.categoryOptions}
            menuInPortal
            data-testid={suffixTestId('category', props)}
          />
        </Box>
        <Box flex={1}>
          <FormField
            control={props.control}
            name="assignMechanic"
            type="choice"
            label={i18n.t('entity.tireSet.labels.mechanic')}
            options={tireSetOptions.mechanicOptions ?? []}
            menuInPortal
            data-testid={suffixTestId('mechanic', props)}
          />
        </Box>
        <Box flex={1}>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="storedUntil"
                type="apiDate"
                label={i18n.t('entity.tireSet.labels.storedUntil')}
                isRelativeDatesHidden
                data-testid={suffixTestId('storedUntil', props)}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name="numberOfTires"
                type="integer"
                label={i18n.t('entity.tireSet.labels.numberOfTires')}
                data-testid={suffixTestId('numberOfTires', props)}
                isRequired
                isStepperVisible
                minStepperValue={props.minNumberOfWheels ?? 1}
                onChange={handleNumberTiresChange}
              />
            </Box>
          </HStack>
        </Box>
        <Box flex={1}>
          <HStack spacing={3}>
            <Show when={props.tireSetId}>
              <Box flex={1}>
                <Show when={tireWarehouseSettings?.isWarehouseAllowed}>
                  <StorageWarehouse
                    tireSetId={props.tireSetId}
                    tireOrderId={props.tireOrderId}
                    isDisabled={!hasReadTireWarehousePermission || isNilOrEmpty(props.tireSetState)}
                    data-testid={suffixTestId('storageWarehouse', props)}
                  />
                </Show>
                <Hide when={tireWarehouseSettings?.isWarehouseAllowed}>
                  <FormField
                    control={props.control}
                    name="storedLocation"
                    type="text"
                    label={i18n.t('entity.tireSet.labels.storedLocation')}
                    data-testid={suffixTestId('storedLocation', props)}
                  />
                </Hide>
              </Box>
            </Show>
            <Box flex={1}>
              <Label data-testid={suffixTestId('isReplacementLabel', props)}>
                {i18n.t('entity.tireSet.labels.additionalOptions')}
              </Label>
              <FormField
                control={props.control}
                name="isReplacement"
                type="checkbox"
                label={i18n.t('entity.tireSet.labels.forReplacement')}
                data-testid={suffixTestId('isReplacement', props)}
              />
            </Box>
          </HStack>
        </Box>
      </HStack>
      <Box flex={1}>
        <FormField
          control={props.control}
          name="note"
          type="text"
          label={i18n.t('general.labels.note')}
          data-testid={suffixTestId('note', props)}
        />
      </Box>
    </VStack>
  );
}
